import { useCallback } from "react";
import { useQuerySearch } from "@/models/search/useQuerySearch";
import * as api from "@/api";
import * as Time from "@/utils/time";

import Api from "@/api";
import { useQuery } from "@tanstack/react-query";
import { Paths } from "../../routes/paths";
export type Person = api.Person;
export { prefetchPersonWithFallback } from "./prefetchPersonWithFallback";
export { useUpdateProfile, useUpdateProfilePicture } from "./profileLifecycle";
export { useUpdateTaskOrdering } from "./taskOrderingLifecycle";

export { useGetTheme, useUpdateTheme } from "./themeLifecycle";
export type { AccountTheme, InviteLink } from "@/api";
export { usePersonFieldSearch } from "./usePersonFieldSearch";
export { useMentionedPersonSearch } from "./useMentionedPersonSearch";
export { usePossibleManagersSearch } from "./usePossibleManagersSearch";

export function useGetPeople(input: api.PeopleListInput) {
  return useQuery(Api.people.listQueryOptions(input));
}

export function useGetBinded(input: api.PeopleGetBindedInput) {
  return useQuery(Api.people.getBindedQueryOptions(input));
}

export function useGetMe(input: api.PeopleGetMeInput) {
  return useQuery(Api.people.getMeQueryOptions(input));
}

export type SearchScope =
  | { type: "company"; id?: undefined }
  | { type: "project"; id: string }
  | { type: "space"; id: string }
  | { type: "goal"; id: string }
  | { type: "resource_hub"; id: string }
  | { type: "none"; id?: undefined };

export const CompanyWideSearchScope = { type: "company" } as SearchScope;
export const NoneSearchScope = { type: "none" } as SearchScope;

export function usePeopleSearch(scope: SearchScope) {
  const search = useQuerySearch((arg: string | { query: string; ignoredIds?: string[] }) => {
    const { query, ignoredIds = [] } = typeof arg === "string" ? { query: arg } : arg;
    return Api.people.searchQueryOptions({ query, ignoredIds, searchScopeType: scope.type, searchScopeId: scope.id });
  }, "");

  return useCallback(
    async (arg: string | { query: string; ignoredIds?: string[] }): Promise<Person[]> => {
      if (scope.type === "none") return [];
      return (await search(arg)).people ?? [];
    },
    [search, scope.type],
  );
}

export function parsePeopleForTurboUi(paths: Paths, people: Person[]) {
  return people
    .map((person) => parsePersonForTurboUi(paths, person))
    .filter((person): person is NonNullable<typeof person> => person !== null);
}

export function parsePersonForTurboUi(paths: Paths, person: Person | null | undefined) {
  if (!person) {
    return null;
  } else {
    return {
      id: person.id,
      fullName: person.fullName,
      email: person.email,
      title: person.title || "",
      avatarUrl: person.avatarUrl || "",
      profileLink: paths.profilePath(person.id),
    };
  }
}

export function firstName(person: Pick<Person, "fullName"> | null | undefined): string {
  const name = person?.fullName?.trim();
  if (!name) return "";

  return name.split(" ")[0]!;
}

export function shortName(person: Pick<Person, "fullName">): string {
  const length = person.fullName!.split(" ").length;

  if (length < 2) return firstName(person);
  return firstName(person) + " " + lastNameInitial(person) + ".";
}

function lastNameInitial(person: Pick<Person, "fullName">): string {
  return person.fullName!.split(" ").slice(-1)[0]![0]!;
}

type NameFormat = "first" | "short" | "full";

export function formattedName(person: Pick<Person, "fullName">, nameFormat: NameFormat): string {
  if (nameFormat === "first") {
    return firstName(person);
  }

  if (nameFormat === "short") {
    return shortName(person);
  }

  return person.fullName!;
}

export function sortByName(people: Person[]): Person[] {
  return people
    .slice()
    .map((p) => p!)
    .sort((a, b) => a!.fullName!.localeCompare(b!.fullName!));
}

export function hasValidInvite(person: Person): boolean {
  if (!person.hasOpenInvitation) return false;
  if (!person.inviteLink?.expiresAt) return false;

  const time = Time.parse(person.inviteLink.expiresAt);
  if (!time) return false;

  return time > Time.now();
}

export function hasInvitationExpired(person: Person): boolean {
  if (!person.hasOpenInvitation) return false;
  if (!person.inviteLink?.expiresAt) return false;

  const time = Time.parse(person.inviteLink.expiresAt);
  if (!time) return false;

  return time < Time.now();
}

export function separatePeople(people?: Person[] | null) {
  people = people || [];

  const isGuest = (person: Person) => person.type === "guest";
  const nonGuests = people.filter((person) => !isGuest(person));
  const guests = people.filter(isGuest);

  return {
    invitedPeople: nonGuests.filter((person) => person.hasOpenInvitation),
    currentMembers: nonGuests.filter((person) => !person.hasOpenInvitation),
    guests,
  };
}
